import { useTheme } from "@mui/material/styles";
import { usePrevious } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useRouter } from "next/router";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { useMemo } from "react";

import { useResponsiveAdSlot } from "scmp-app/components/advertisement/hooks";
import { appBarScrollStickyAtom } from "scmp-app/components/app-bar/hooks/sticky/atoms";
import { useScrollOverThreshold } from "scmp-app/lib/hooks/use-scroll-over-threshold";
import { HeaderBottomContainerElementID } from "scmp-app/pages/_app/consts";

import { appBarAtom } from "./atoms";
import { HideElementOnScrollEffectThreshold } from "./consts";
import { AppBarContextProvider } from "./contexts";
import { getShouldShowAdBelowAppBar } from "./helpers";
import { useAppBarSetStickyHeight } from "./hooks";
import type { AppBarStickyHeightContract } from "./hooks/sticky/app-bar-set-sticky-height";
import {
  AdSlotContainer,
  Container,
  FullWidthBackground,
  HeaderBottomContainer,
  StyledHeader,
  StyledHomeHeader,
  StyledPlusHeader,
} from "./styles";
import type { AppBarVariant } from "./types";

export type Props = {
  children?: ReactNode;
  className?: string;
  hideTargetChildren?: ReactNode;
  pageProps?: PageProps;
};

export const AppBar: FunctionComponent<Props> = ({ children, className, pageProps }) => {
  const router = useRouter();
  const { adSlotHeight, bindAdSlotContainer, desktopAdSlot, mobileAdSlot, stickyStyle } =
    useResponsiveAdSlot(pageProps?.appBarConfiguration);

  const appBarValue = useAtomValue(appBarAtom);
  const { stickyScrollHeight = 0 } = useAtomValue(appBarScrollStickyAtom);
  const appBarStickyScrollHeight = useMemo(
    // added 2 trying to avoid double menu when scrolling up/down, should find a better way.
    () => stickyScrollHeight + adSlotHeight,
    [adSlotHeight, stickyScrollHeight],
  );
  const appBarVariant = useMemo<AppBarVariant>(
    () =>
      appBarValue?.scrollOverVariant ??
      pageProps?.appBarConfiguration?.variant ??
      "scmp/generic-light",
    [appBarValue?.scrollOverVariant, pageProps?.appBarConfiguration?.variant],
  );

  const isGamePages = router.pathname.startsWith("/games");

  // SCMPWEB-10011 T2-B: each breakpoint only creates the sentinel its ACTIVE
  // style consumes (desktop = scroll-away; tablet/mobile = hide-threshold) —
  // passing `null` gates subscription via useScrollOverThreshold and avoids
  // dormant sentinels that would confuse the ?debug-sentinels=1 view.
  const isHideStyleActive = stickyStyle === "withHideElementAfterScrollThreshold";
  const hideElementOnScrollEffect = useStickyWithHideElementAfterScrollThreshold(
    isHideStyleActive ? HideElementOnScrollEffectThreshold.mobile : null,
    appBarStickyScrollHeight,
  );
  const stickyWithScrollAwayElement = useStickyWithScrollAwayElement(
    // Active branch: master's semantic at a zero sliver height was
    // `window.scrollY > 0` — stick past ANY scroll — so clamp to 1 to keep
    // the sentinel subscribed (the hook's null/≤0 gate would otherwise skip
    // creation and the container would never engage `position: sticky`;
    // the whole masthead then scrolls away naturally — the regression the
    // user saw). `null`: the active sticky style is hide-threshold on this
    // breakpoint.
    isHideStyleActive ? null : Math.max(appBarStickyScrollHeight, 1),
  );

  const styles: CSSProperties = {
    ...(isHideStyleActive
      ? hideElementOnScrollEffect.targetContainerStyles
      : stickyWithScrollAwayElement.targetContainerStyles),
  };

  // Master parity contract for `useAppBarSetStickyHeight` — mirrors the variant styles
  // applied above (see `AppBarStickyHeightContract`:
  // - hide variant: sticky from mount, flips to `translateY(-appBarStickyScrollHeight)` at
  //   `HideElementOnScrollEffectThreshold` — fully visible before the flip, sliver hidden after.
  // - scroll-away variant: `position: relative` until `appBarStickyScrollHeight`, then
  //   `position: sticky` + translate of the same distance.
  // - game pages: the container renders as `position: relative` with no translate — never sticky.
  const appBarStickyHeightContract: AppBarStickyHeightContract = useMemo(
    () =>
      stickyStyle === "withHideElementAfterScrollThreshold"
        ? isGamePages
          ? null
          : {
              flipThresholdPx: HideElementOnScrollEffectThreshold.mobile,
              notFlippedValue: "appBarHeight",
              translateDistancePx: appBarStickyScrollHeight,
            }
        : {
            flipThresholdPx: appBarStickyScrollHeight,
            notFlippedValue: 0,
            translateDistancePx: appBarStickyScrollHeight,
          },
    [appBarStickyScrollHeight, isGamePages, stickyStyle],
  );

  const { reference } = useAppBarSetStickyHeight(appBarStickyHeightContract);

  const headerConfiguration = {
    ...pageProps?.headerConfiguration,
    appBarStickyScrollHeight,
    appBarVariant,
  };

  const renderHeader = () => {
    switch (appBarVariant) {
      case "none":
        return null;
      case "scmp/home":
        return (
          <StyledHomeHeader
            headerConfiguration={headerConfiguration}
            initEdition={pageProps?.initEdition}
          />
        );
      case "scmp/plus":
      case "scmp/plus/learnmore":
        return <StyledPlusHeader headerConfiguration={headerConfiguration} />;
      default:
        return (
          <StyledHeader
            headerConfiguration={headerConfiguration}
            initEdition={pageProps?.initEdition}
          />
        );
    }
  };

  const renderLayout = () => {
    switch (appBarVariant) {
      default:
        return getShouldShowAdBelowAppBar(appBarVariant) ? (
          <>
            {renderHeader()}
            <HeaderBottomContainer id={HeaderBottomContainerElementID} />
            {children}
            <AdSlotContainer {...bindAdSlotContainer}>
              {mobileAdSlot}
              {desktopAdSlot}
            </AdSlotContainer>
          </>
        ) : (
          <>
            <AdSlotContainer {...bindAdSlotContainer} $hasBorderBottom={true}>
              {mobileAdSlot}
              {desktopAdSlot}
            </AdSlotContainer>
            {renderHeader()}
            <HeaderBottomContainer id={HeaderBottomContainerElementID} />
            {children}
          </>
        );
    }
  };

  return (
    <AppBarContextProvider>
      <Container
        $appBarVariant={appBarVariant}
        className={className}
        ref={reference}
        style={{ ...styles }}
      >
        <FullWidthBackground />
        {renderLayout()}
      </Container>
    </AppBarContextProvider>
  );
};

AppBar.displayName = "AppBar";

// This effect will hide the element with transition after the scroll container scroll passes a threshold
const useStickyWithHideElementAfterScrollThreshold = (
  // `null`: the active sticky style is scroll-away on this breakpoint — the
  // sentinel is not created and the hook's output is unused.
  scrollThreshold: null | number,
  targetElementHeight: number,
) => {
  const router = useRouter();
  const isGamePages = router.pathname.startsWith("/games");

  const theme = useTheme();
  const previousTargetElementHeight = usePrevious(targetElementHeight);

  const isScrollingOverThreshold = useScrollOverThreshold(
    scrollThreshold,
    "AppBar hide-threshold (mobile value — used by hide-style variants)",
  );
  const shouldPauseTransition = previousTargetElementHeight !== targetElementHeight;

  // Do not sticky in game pages
  const targetContainerStyles: CSSProperties = isGamePages
    ? {
        position: "relative",
      }
    : {
        position: "sticky",
        transform: isScrollingOverThreshold ? `translateY(-${targetElementHeight}px)` : "none",
        transition: shouldPauseTransition
          ? "none"
          : theme.transitions.create("transform", {
              duration: 50,
            }),
      };

  return {
    targetContainerStyles,
  };
};

// This effect will make the container sticky after the scroll away element is complete scroll
// passed in the scroll container
const useStickyWithScrollAwayElement = (
  // `null`: the active sticky style is hide-threshold on this breakpoint —
  // the sentinel is not created and the hook's output is unused.
  scrollAwayElementHeight: null | number,
) => {
  const shouldStick = useScrollOverThreshold(
    scrollAwayElementHeight,
    "AppBar sticky-after-scroll-away (masthead/app-bar height + ad slot)",
  );

  const targetContainerStyles: CSSProperties = {
    position: shouldStick ? "sticky" : "relative",
    transform: shouldStick ? `translateY(-${scrollAwayElementHeight}px)` : "none",
  };

  return {
    targetContainerStyles,
  };
};
